import { defineStore } from 'pinia'
import { ref, computed, readonly } from 'vue'
import { productsAPI } from '../api/modules/products.js'

export const useProductsStore = defineStore('products', () => {
  // State
  const products = ref([])
  const currentProduct = ref(null)
  const categories = ref([])
  const filters = ref({
    search: '',
    category: null,
    minPrice: null,
    maxPrice: null,
    inStock: null,
    onSale: null,
    featured: null
  })
  const pagination = ref({
    currentPage: 1,
    lastPage: 1,
    perPage: 15,
    total: 0
  })
  const isLoading = ref(false)
  const error = ref(null)

  // Computed
  const hasProducts = computed(() => products.value.length > 0)
  const filteredProductsCount = computed(() => pagination.value.total)
  const hasMorePages = computed(() => pagination.value.currentPage < pagination.value.lastPage)
  const currentPageProducts = computed(() => products.value)

  // Actions
  const setProducts = (productsData) => {
    products.value = productsData
  }

  const appendProducts = (productsData) => {
    products.value = [...products.value, ...productsData]
  }

  const setCurrentProduct = (product) => {
    currentProduct.value = product
  }

  const setCategories = (categoriesData) => {
    categories.value = categoriesData
  }

  const setFilters = (newFilters) => {
    filters.value = { ...filters.value, ...newFilters }
  }

  const resetFilters = () => {
    filters.value = {
      search: '',
      category: null,
      minPrice: null,
      maxPrice: null,
      inStock: null,
      onSale: null,
      featured: null
    }
  }

  const setPagination = (paginationData) => {
    pagination.value = {
      currentPage: paginationData.current_page || 1,
      lastPage: paginationData.last_page || 1,
      perPage: paginationData.per_page || 15,
      total: paginationData.total || 0
    }
  }

  const getProducts = async (params = {}) => {
    try {
      isLoading.value = true
      error.value = null
      
      const queryParams = {
        ...filters.value,
        ...params,
        page: params.page || pagination.value.currentPage
      }
      
      // Remove null/empty values
      Object.keys(queryParams).forEach(key => {
        if (queryParams[key] === null || queryParams[key] === '') {
          delete queryParams[key]
        }
      })
      
      const response = await productsAPI.getProducts(queryParams)
      
      if (params.append) {
        appendProducts(response.data)
      } else {
        setProducts(response.data)
      }
      
      setPagination(response.meta)
      
      return { success: true, data: response.data, meta: response.meta }
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to load products'
      return { success: false, error: error.value }
    } finally {
      isLoading.value = false
    }
  }

  const getProduct = async (slug, forceRefresh = false) => {
    try {
      isLoading.value = true
      error.value = null
      
      // Check if we already have this product and don't need to refresh
      if (!forceRefresh && currentProduct.value?.slug === slug) {
        isLoading.value = false
        return { success: true, data: currentProduct.value }
      }
      
      const response = await productsAPI.getProduct(slug)
      
      setCurrentProduct(response.data)
      
      // Also update in products list if it exists there
      const productIndex = products.value.findIndex(p => p.slug === slug)
      if (productIndex !== -1) {
        products.value[productIndex] = response.data
      }
      
      return { success: true, data: response.data }
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to load product'
      return { success: false, error: error.value }
    } finally {
      isLoading.value = false
    }
  }

  const getBulkDiscount = async (productId) => {
    try {
      const response = await productsAPI.getBulkDiscount(productId)
      return { success: true, data: response.data }
    } catch (err) {
      return { success: false, error: err.response?.data?.message || 'Failed to get bulk discount' }
    }
  }

  const searchProducts = async (searchTerm) => {
    setFilters({ search: searchTerm })
    return await getProducts({ page: 1 })
  }

  const filterByCategory = async (categoryId) => {
    setFilters({ category: categoryId })
    return await getProducts({ page: 1 })
  }

  const filterByPrice = async (minPrice, maxPrice) => {
    setFilters({ minPrice, maxPrice })
    return await getProducts({ page: 1 })
  }

  const loadMoreProducts = async () => {
    if (hasMorePages.value) {
      const nextPage = pagination.value.currentPage + 1
      return await getProducts({ page: nextPage, append: true })
    }
    return { success: false, error: 'No more pages' }
  }

  // Helper functions
  const findProductById = (productId) => {
    return products.value.find(product => product.id === productId)
  }

  const findProductBySlug = (slug) => {
    return products.value.find(product => product.slug === slug)
  }

  const updateProduct = (productId, updatedData) => {
    const index = products.value.findIndex(product => product.id === productId)
    if (index !== -1) {
      products.value[index] = { ...products.value[index], ...updatedData }
    }
  }

  const removeProduct = (productId) => {
    const index = products.value.findIndex(product => product.id === productId)
    if (index !== -1) {
      products.value.splice(index, 1)
    }
  }

  const addProduct = (product) => {
    products.value.unshift(product)
  }

  const getProductsByCategory = (categoryId) => {
    return products.value.filter(product => 
      product.categories?.some(cat => cat.id === categoryId)
    )
  }

  const getFeaturedProducts = () => {
    return products.value.filter(product => product.is_propagated)
  }

  const getSaleProducts = () => {
    return products.value.filter(product => product.is_on_sale)
  }

  const getNewProducts = () => {
    // Assuming products created in last 30 days are "new"
    const thirtyDaysAgo = new Date()
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
    
    return products.value.filter(product => 
      new Date(product.created_at) > thirtyDaysAgo
    )
  }

  const clearProducts = () => {
    products.value = []
    currentProduct.value = null
    error.value = null
  }

  return {
    // State
    products: readonly(products),
    currentProduct: readonly(currentProduct),
    categories: readonly(categories),
    filters: readonly(filters),
    pagination: readonly(pagination),
    isLoading: readonly(isLoading),
    error: readonly(error),
    
    // Computed
    hasProducts,
    filteredProductsCount,
    hasMorePages,
    currentPageProducts,
    
    // Actions
    setProducts,
    appendProducts,
    setCurrentProduct,
    setCategories,
    setFilters,
    resetFilters,
    setPagination,
    getProducts,
    getProduct,
    getBulkDiscount,
    searchProducts,
    filterByCategory,
    filterByPrice,
    loadMoreProducts,
    
    // Helpers
    findProductById,
    findProductBySlug,
    updateProduct,
    removeProduct,
    addProduct,
    getProductsByCategory,
    getFeaturedProducts,
    getSaleProducts,
    getNewProducts,
    clearProducts
  }
})
